/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { inject, Injectable } from '@angular/core';
import { Action, ActionReducerMap, combineReducers, ReducerManager, Store } from '@ngrx/store';
import { ResourceUri } from '@ngxp/rest';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CreateResourceData, StateInfo, StateLoader } from '../resource/resource.model';
import { StateResource } from '../resource/resource.util';
import { isNotNil, isNotUndefined } from '../tech.util';
import {
  createListResourceActions,
  createSingleResourceActions,
  ListResourceLoadActions,
  ResourceActions,
  SingleResourceLoadActions,
} from './actions';
import { EffectService } from './effects.service';
import { ListResourceReducer } from './list-resource.reducer';
import { SingleResourceReducer } from './resource.reducer';
import { ResourceSelectors } from './selector';
import { AdditionalFeatureKey, FeatureInfo, ListResourceState, SingleResourceState, StoreFeature } from './state.model';

@Injectable({ providedIn: 'root' })
export class StateService {
  private readonly store: Store = inject(Store);
  private readonly effectService: EffectService = inject(EffectService);
  private readonly reducerManager: ReducerManager = inject(ReducerManager);

  public createSingleResourceService<T>(stateInfo: StateInfo): SingleResourceStateService<T> {
    return new SingleResourceStateService(stateInfo, this.store, this.effectService);
  }

  public createListResourceStateService<T>(stateInfo: StateInfo): ListResourceStateService<T> {
    return new ListResourceStateService(stateInfo, this.store, this.effectService);
  }

  public registerFeature<S>(featureInfo: FeatureInfo): void {
    this.reducerManager.addFeature(this._buildFeature<S>(featureInfo));
  }

  _buildFeature<S>(featureInfo: FeatureInfo): StoreFeature<S> {
    return {
      key: featureInfo.stateName,
      reducers: this.buildReducerMap<S>(featureInfo),
      reducerFactory: combineReducers,
    };
  }

  private buildReducerMap<S>(featureInfo: FeatureInfo): ActionReducerMap<S> {
    const reducer: ActionReducerMap<S> = <ActionReducerMap<S>>{
      [featureInfo.resourcePath]: this.buildSingleReducer(featureInfo),
      [featureInfo.listResourcePath]: this.buildListReducer(featureInfo),
    };
    if (isNotUndefined(featureInfo.additionalFeature)) {
      featureInfo.additionalFeature.forEach((feature: AdditionalFeatureKey) => {
        reducer[feature.key] = isNotUndefined(reducer[feature.key])
          ? this.composeReducers(reducer[feature.key], feature.reducerFn)
          : feature.reducerFn;
      });
    }
    return reducer;
  }

  private composeReducers<S, A extends Action = Action>(
    baseReducer: (state: S, action: A) => S,
    additionalReducer: (state: S, action: A) => S,
  ): (state: S, action: A) => S {
    return (state: S, action: A) => additionalReducer(baseReducer(state, action), action);
  }

  private buildSingleReducer(featureInfo: FeatureInfo): (state: SingleResourceState, action: Action) => SingleResourceState {
    return (state: SingleResourceState, action: Action) => this.createSingleResourceReducer(featureInfo).reducer(state, action);
  }

  private createSingleResourceReducer(featureInfo: FeatureInfo): SingleResourceReducer {
    return new SingleResourceReducer(
      createSingleResourceActions({ name: featureInfo.stateName, path: featureInfo.resourcePath }),
      featureInfo.concurrentModificationAction,
    );
  }

  private buildListReducer(featureInfo: FeatureInfo): (state: ListResourceState, action: Action) => ListResourceState {
    return (state: ListResourceState, action: Action) => this.createListResourceReducer(featureInfo).reducer(state, action);
  }

  private createListResourceReducer(featureInfo: FeatureInfo): ListResourceReducer {
    return new ListResourceReducer(
      createListResourceActions({ name: featureInfo.stateName, path: featureInfo.listResourcePath }),
      featureInfo.concurrentModificationAction,
    );
  }
}

export abstract class ResourceStateService<T> implements StateLoader<T> {
  actions: ResourceActions;

  constructor(
    protected stateInfo: StateInfo,
    protected store: Store,
    protected effectService: EffectService,
  ) {
    this.init();
  }

  public init(): void {
    this.initActions();
    this.initEffects();
  }

  protected abstract initActions(): void;

  protected abstract initEffects(): void;

  public clearResource(): void {
    this.store.dispatch(this.actions.clearAction());
  }

  public loadResource(resourceUri: ResourceUri): void {
    this.store.dispatch(this.actions.loadAction({ resourceUri }));
  }

  public reloadResource(): void {
    this.store.dispatch(this.actions.reloadAction());
  }

  public abstract selectResource(): Observable<StateResource<T>>;
}

export class SingleResourceStateService<T> extends ResourceStateService<T> {
  declare actions: SingleResourceLoadActions;

  constructor(
    protected stateInfo: StateInfo,
    protected store: Store,
    protected effectService: EffectService,
  ) {
    super(stateInfo, store, effectService);
  }

  protected initActions(): void {
    this.actions = createSingleResourceActions(this.stateInfo);
  }

  protected initEffects(): void {
    this.effectService.addSingleResourceEffects(this.actions);
  }

  public selectResource(): Observable<StateResource<T>> {
    return this.store.select(ResourceSelectors.selectResource(this.stateInfo));
  }

  public existsResource(): Observable<boolean> {
    return this.store.select(ResourceSelectors.existResource(this.stateInfo));
  }
}

export class ListResourceStateService<T> extends ResourceStateService<T> {
  declare actions: ListResourceLoadActions;

  constructor(
    protected stateInfo: StateInfo,
    protected store: Store,
    protected effectService: EffectService,
  ) {
    super(stateInfo, store, effectService);
  }

  protected initActions(): void {
    this.actions = createListResourceActions(this.stateInfo);
  }

  protected initEffects(): void {
    this.effectService.addListResourceEffects(this.actions);
  }

  public selectResource<T>(): Observable<StateResource<T>> {
    return this.store.select(ResourceSelectors.selectListResource(this.stateInfo));
  }

  public create<T>(createResourceData: CreateResourceData): Observable<StateResource<T>> {
    this.store.dispatch(this.actions.createListItemAction({ payload: createResourceData }));
    return this.store.select(ResourceSelectors.selectCreatedListItemResource(this.stateInfo)).pipe(filter(isNotNil));
  }
}
